import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { useAtomValue } from "jotai";
import { isEmpty } from "lodash-es";
import { useContext } from "react";

import { handleAuthzRefusal } from "~/access/authzProblem";
import { userAtom, zitadelOrgAtom } from "~/atoms";
import { useQueriesErrorEffect, useQueryErrorEffect } from "~/hooks";
import { isWorkOSAuth } from "~/lib/authProvider";
import {
  CLIENT_VERSION,
  CLIENT_VERSION_HEADER,
  handleUpgradeRequired,
} from "~/lib/clientVersion";
import { ensureFreshSession } from "~/lib/refreshCoordinator";
import { SnackbarContext } from "~/utils/Contexts";
import { Env } from "~/utils/Env";
import { optimiseQueryResult } from "~/utils/Helper";
import { ResponseError } from "~/utils/ResponseError";
import {
  MessageText,
  QueryResult,
  ServerResponseStatusCode,
} from "~/utils/Types";

export { useQueryClient } from "@tanstack/react-query";

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Transformers operate on untyped API response data that varies per endpoint
export type Transformer = (data: any) => any;

type CreateAxiosRequestArgs = {
  config: AxiosRequestConfig;
  path: string;
  transformers: Transformer[];
  baseUrl?: string;
  user: { access_token: string } | null | undefined;
  zitadelOrg: { idString: string } | null | undefined;
};

/**
 * Core axios request logic shared by useAxios and useApiQueries
 * Handles authentication, transformers, and error handling
 *
 * Exported for use in utility functions that need axios requests
 * outside of React components (e.g., auth flows, event handlers)
 */
export const createAxiosRequest = <T>({
  config,
  path,
  transformers,
  baseUrl,
  user,
  zitadelOrg,
}: CreateAxiosRequestArgs): Promise<T> => {
  const isWorkos = isWorkOSAuth();
  // Binary multipart uploads (FormData) must NOT be sent as application/json:
  // axios needs to own the Content-Type so it can append the multipart
  // boundary. We drop the default JSON Content-Type for FormData bodies and
  // let axios fill it in. Auth/org headers are preserved. The `config` spread
  // is shallow, so we deep-merge headers here to keep callers from having to
  // re-supply Authorization/zitadelOrgIdString when overriding a header.
  const isFormData =
    typeof FormData !== "undefined" && config.data instanceof FormData;
  const { headers: configHeaders, ...restConfig } = config;
  const axiosConfig: AxiosRequestConfig = {
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      Accept: "application/json",
      [CLIENT_VERSION_HEADER]: String(CLIENT_VERSION),
      Authorization: !isWorkos && user ? `Bearer ${user.access_token}` : null,
      zitadelOrgIdString: isWorkos ? "" : (zitadelOrg?.idString ?? ""),
      ...configHeaders,
    },
    withCredentials: isWorkos,
    url:
      (baseUrl ??
        (Env.VITE_API_ENDPOINT || `https://${window.location.hostname}/api`)) +
      path,
    ...restConfig,
  };

  const attempt = () =>
    axios(axiosConfig).then((response) => {
      let data = response.data;
      for (const transformer of transformers) {
        data = transformer(data);
      }
      return data;
    });

  const onError = (error: AxiosError) => {
    if (
      Env.VITE_ENV === "development" &&
      error.constructor.name !== "CanceledError"
    ) {
      console.error("api error", error, path);
    }
    // Transformer failures (a 2xx response whose body carries code FAILED and
    // a msg, thrown as ResponseError by decodeSuccessTransformer) land here
    // too. Rethrow them untouched so the server's msg reaches the error
    // snackbar instead of being replaced with the generic network error.
    if (error instanceof ResponseError) {
      throw error;
    }
    if (error.constructor.name === "CanceledError") {
      // noop
    } else if (error.response?.data) {
      console.error(error);
      // Problem Details bodies (RFC 9457) carry `detail`/`title`, not `msg`.
      const body = error.response.data as {
        msg?: string;
        detail?: string;
        title?: string;
      };
      throw new ResponseError(
        (body.msg ?? body.detail ?? body.title) as string,
        error.response?.status,
        typeof error.response.data === "string"
          ? error.response.data
          : undefined,
      );
    } else {
      console.error(error);
      throw new ResponseError(
        MessageText.NETWORK_ERROR,
        error.response?.status,
      );
    }
  };

  // A 426 and an authorization 403 must be caught before onError: their
  // Problem Details bodies carry no `msg`, so the generic mapping would surface
  // `undefined` in the snackbar.
  const onFailure = (error: AxiosError) =>
    handleUpgradeRequired(error) ?? handleAuthzRefusal(error) ?? onError(error);

  // Legacy (Zitadel) path: single attempt, unchanged behaviour.
  if (!isWorkos) return attempt().catch(onFailure);

  // WorkOS path (N5 — silent refresh reactive trigger): a 401 means meq_at
  // expired or was rejected. Funnel through ensureFreshSession (single-flight
  // refresh) and retry the request exactly once. A persistent 401 after the
  // refresh falls through to the normal error mapping, which logs out. A
  // refresh that threw left the session in place (network, 5xx), so the
  // original 401 must not surface — every 401 consumer logs out on it.
  return attempt().catch((error: AxiosError) => {
    if (error?.response?.status !== 401) return onFailure(error);
    return ensureFreshSession({ confidence: "known", reactive: true }).then(
      () => attempt().catch(onFailure),
      () => {
        throw new ResponseError(MessageText.NETWORK_ERROR, 503);
      },
    );
  });
};

type UseAxiosArgs = {
  config?: AxiosRequestConfig;
  path: string;
  transformers?: Transformer[];
  baseUrl?: string;
};

export const useAxios = <T>({
  config = {},
  path,
  transformers = [],
  baseUrl,
}: UseAxiosArgs): ((extraConfig?: AxiosRequestConfig) => Promise<T>) => {
  const user = useAtomValue(userAtom);
  const zitadelOrg = useAtomValue(zitadelOrgAtom);

  return (extraConfig = {}) =>
    createAxiosRequest<T>({
      config: { ...config, ...extraConfig },
      path,
      transformers,
      baseUrl,
      user,
      zitadelOrg,
    });
};

export const decodeSuccessTransformer: Transformer = (data) => {
  if (data.code === ServerResponseStatusCode.SUCCESS) {
    // Handle empty/undefined data field - return empty array for React Query
    // (React Query doesn't allow undefined return values)
    if (!data.data) {
      return [];
    }
    return JSON.parse(data.data);
  }
  throw new ResponseError(data.msg);
};

type Snackbar = React.ContextType<typeof SnackbarContext>;

const decodeShowSuccessSnackbarTransformer: (
  snackbar: Snackbar,
) => Transformer = (snackbar: Snackbar) => (data) => {
  if (data.code === ServerResponseStatusCode.SUCCESS) {
    snackbar.open({ text: data.msg, type: "success" });
    if (isEmpty(data.data)) {
      return;
    }
    return JSON.parse(data.data);
  }
  throw new ResponseError(data.msg);
};

const showSuccessSnackbarTransformer: (
  snackbar: Snackbar,
  msg: string,
) => Transformer = (snackbar, msg) => (data) => {
  snackbar.open({ text: msg, type: "success" });
  return data;
};

const optimiseQueryResultTransformer: Transformer = (data) =>
  optimiseQueryResult(data);

type UseApiQueryArgs<T> = {
  config?: AxiosRequestConfig;
  enabled?: boolean;
  options?: Partial<UseQueryOptions<T, Error>>;
  path: string;
  refetchOnWindowFocus?: boolean;
  shouldDecodeSuccess?: boolean;
  shouldDecodeAndShowSuccess?: boolean;
  shouldOptimiseQueryResult?: boolean;
  /** Set false when the caller renders the error inline instead. */
  shouldShowErrorSnackbar?: boolean;
  transformers?: Transformer[];
  baseUrl?: string;
};

export const useApiQuery = <T = QueryResult>({
  config = {},
  enabled = true,
  options = {},
  path,
  refetchOnWindowFocus,
  shouldDecodeSuccess,
  shouldDecodeAndShowSuccess,
  shouldOptimiseQueryResult,
  shouldShowErrorSnackbar = true,
  transformers = [],
  baseUrl,
}: UseApiQueryArgs<T>) => {
  const snackbar = useContext(SnackbarContext);
  if (shouldDecodeSuccess) transformers.push(decodeSuccessTransformer);
  if (shouldDecodeAndShowSuccess) {
    transformers.push(decodeShowSuccessSnackbarTransformer(snackbar));
  }
  if (shouldOptimiseQueryResult) {
    transformers.push(optimiseQueryResultTransformer);
  }
  const queryFn = useAxios<T>({
    config: { ...config },
    path,
    transformers,
    baseUrl,
  });

  const queryKey = [path, config.data, config.params];

  const queryResult = useQuery<T, Error>({
    queryKey,
    queryFn,
    enabled: enabled,
    retry: false,
    refetchOnWindowFocus,
    ...options,
  });

  useQueryErrorEffect(queryResult, {
    showErrorSnackbar: shouldShowErrorSnackbar,
  });

  return {
    ...queryResult,
    queryKey,
  };
};

type UseApiMutationArgs<T> = {
  config?: AxiosRequestConfig;
  options?: UseMutationOptions<T, Error, AxiosRequestConfig>;
  path: string;
  shouldDecodeSuccess?: boolean;
  shouldDecodeAndShowSuccess?: boolean;
  shouldOptimiseQueryResult?: boolean;
  shouldShowSuccessMessage?: string;
  /** Set false when the caller renders the error inline instead. */
  shouldShowErrorSnackbar?: boolean;
  transformers?: Transformer[];
};

export const useApiMutation = <T>({
  config = {},
  options = {},
  path,
  shouldDecodeSuccess,
  shouldDecodeAndShowSuccess,
  shouldOptimiseQueryResult,
  shouldShowSuccessMessage,
  shouldShowErrorSnackbar = true,
  transformers = [],
}: UseApiMutationArgs<T>) => {
  const snackbar = useContext(SnackbarContext);
  if (shouldDecodeSuccess) transformers.push(decodeSuccessTransformer);
  if (shouldDecodeAndShowSuccess) {
    transformers.push(decodeShowSuccessSnackbarTransformer(snackbar));
  }
  if (shouldOptimiseQueryResult) {
    transformers.push(optimiseQueryResultTransformer);
  }
  if (shouldShowSuccessMessage) {
    transformers.push(
      showSuccessSnackbarTransformer(snackbar, shouldShowSuccessMessage),
    );
  }
  const mutationFn = useAxios<T>({
    config: { ...config },
    path,
    transformers,
  });

  const queryResult = useMutation<T, Error, AxiosRequestConfig>({
    mutationFn,
    retry: false,
    ...options,
  });

  useQueryErrorEffect(queryResult, {
    showErrorSnackbar: shouldShowErrorSnackbar,
  });

  return queryResult;
};

type UseApiQueriesArgs<T> = {
  config?: AxiosRequestConfig;
  enabled?: boolean;
  options?: Partial<UseQueryOptions<T, Error>>;
  path: string;
  refetchOnWindowFocus?: boolean;
  shouldDecodeSuccess?: boolean;
  shouldDecodeAndShowSuccess?: boolean;
  shouldOptimiseQueryResult?: boolean;
  transformers?: Transformer[];
  baseUrl?: string;
};

/**
 * Custom hook for running multiple API queries in parallel
 * Works just like useApiQuery but accepts an array of query configs
 *
 * @example
 * const results = useApiQueries([
 *   { path: '/api/users', config: { method: 'GET' } },
 *   { path: '/api/posts', config: { method: 'GET' } },
 * ]);
 */
export const useApiQueries = <T = QueryResult>(
  queries: UseApiQueriesArgs<T>[],
) => {
  const snackbar = useContext(SnackbarContext);
  const user = useAtomValue(userAtom);
  const zitadelOrg = useAtomValue(zitadelOrgAtom);

  const queryResults = useQueries({
    queries: queries.map((queryConfig) => {
      const {
        config = {},
        enabled = true,
        options = {},
        path,
        refetchOnWindowFocus,
        shouldDecodeSuccess,
        shouldDecodeAndShowSuccess,
        shouldOptimiseQueryResult,
        transformers = [],
        baseUrl,
      } = queryConfig;

      // Build transformers array
      const finalTransformers = [...transformers];
      if (shouldDecodeSuccess) finalTransformers.push(decodeSuccessTransformer);
      if (shouldDecodeAndShowSuccess) {
        finalTransformers.push(decodeShowSuccessSnackbarTransformer(snackbar));
      }
      if (shouldOptimiseQueryResult) {
        finalTransformers.push(optimiseQueryResultTransformer);
      }

      // Create query function using shared createAxiosRequest
      const queryFn = () =>
        createAxiosRequest<T>({
          config,
          path,
          transformers: finalTransformers,
          baseUrl,
          user,
          zitadelOrg,
        });

      const queryKey = [path, config.data, config.params];

      return {
        queryKey,
        queryFn,
        enabled,
        retry: false,
        refetchOnWindowFocus,
        ...options,
      };
    }),
  });

  // Apply error effects to all query results
  useQueriesErrorEffect(queryResults);

  return queryResults;
};
